/* ──────────────────────────────────────────────────────────────────────────
   The window the Now page is looking at: Today, Yesterday, 7D, 30D, or two
   dates picked on the calendar.

   One definition for the page and for every module that answers it, so "7D"
   cannot mean seven days to one card and eight to the next. Pure, with no
   imports, because the web app reads it too (as @signet/kernel/now-range.ts)
   and must not pull the server half of the kernel into the bundle.

   Days are plain YYYY-MM-DD strings. Which day is "today" is the caller's
   question: Links answers it in Melbourne, Reporting from its own snapshots.
   ────────────────────────────────────────────────────────────────────────── */

export const NOW_RANGE_KEYS = ["today", "yesterday", "7d", "30d", "custom"] as const;
export type NowRangeKey = (typeof NOW_RANGE_KEYS)[number];
export type NowRange = { key: NowRangeKey; from?: string; to?: string };

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_DAY = 86_400_000;
/** A hand-typed year-long custom range is the most anyone reads on a home screen. */
const LONGEST_CUSTOM = 366;

const isDay = (value: unknown): value is string =>
  typeof value === "string" && DAY.test(value) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;

export function shiftDay(day: string, offset: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + offset * MS_PER_DAY).toISOString().slice(0, 10);
}

export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / MS_PER_DAY);
}

/** From a query string or the page's search params. Anything unreadable is Today, never an error. */
export function parseNowRange(input: { range?: unknown; from?: unknown; to?: unknown }): NowRange {
  const key = String(input.range ?? "").toLowerCase();
  if (key === "custom") {
    const { from, to } = input;
    if (isDay(from) && isDay(to) && from <= to && daysBetween(from, to) < LONGEST_CUSTOM) return { key: "custom", from, to };
    return { key: "today" };
  }
  return (NOW_RANGE_KEYS as readonly string[]).includes(key) ? { key: key as NowRangeKey } : { key: "today" };
}

/** The same range as query parameters, for a module's API. */
export function nowRangeQuery(range: NowRange): string {
  const params = new URLSearchParams({ range: range.key });
  if (range.key === "custom" && range.from && range.to) {
    params.set("from", range.from);
    params.set("to", range.to);
  }
  return params.toString();
}

/**
 * The days a range covers, counted back from `today`, and the equally long
 * window just before it, which every card compares against.
 */
export function nowRangeDays(range: NowRange, today: string) {
  const window = (() => {
    if (range.key === "yesterday") return { from: shiftDay(today, -1), to: shiftDay(today, -1) };
    if (range.key === "7d") return { from: shiftDay(today, -6), to: today };
    if (range.key === "30d") return { from: shiftDay(today, -29), to: today };
    if (range.key === "custom" && range.from && range.to) return { from: range.from, to: range.to };
    return { from: today, to: today };
  })();
  const length = daysBetween(window.from, window.to) + 1;
  return { ...window, previousFrom: shiftDay(window.from, -length), previousTo: shiftDay(window.from, -1) };
}

/**
 * What the change beside each figure is measured against. One wording for every
 * card on Now (Jay, 2026-10-13): Clicks said "vs the day before" while Revenue
 * said "vs the day before that" for the same Yesterday. A day is always "vs the
 * day before"; longer windows are short enough to sit beside a figure.
 */
export function nowRangeVersus(range: NowRange): string {
  if (range.key === "today" || range.key === "yesterday") return "vs the day before";
  if (range.key === "custom" && range.from && range.from === range.to) return "vs the day before";
  if (range.key === "7d") return "vs prior 7 days";
  if (range.key === "30d") return "vs prior 30 days";
  return "vs prior period";
}
